"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Edit2 } from "lucide-react";
import type { JsonEditorModalData } from "@/lib/modals/data/modal-data-types";
import {
  parseJSONSafely,
  objectToKeyValuePairs,
  keyValuePairsToObject,
  getJSONParseError,
  validateKeyValuePair,
} from "@/lib/utils/json-field-utils";
import { KeyValuePairRow } from "./KeyValuePairRow";
import { AddKeyValueForm } from "./AddKeyValueForm";
import { JsonErrorDisplay } from "./JsonErrorDisplay";
import { toast } from "sonner";
import type { JsonFieldValue, JsonKeyValuePair, JsonValue } from "@/components/shared/types";

interface JsonFieldEditorProps {
  value: JsonFieldValue;
  fieldId: string;
  onSave: (newValue: Record<string, JsonValue>) => Promise<void>;
  readOnlyMode?: boolean;
  globalModalId?: string;
}

/**
 * Main JSON field editor component
 * Opens in a modal using the global modal stack
 * Handles display, editing, and validation of JSON key-value pairs
 */
export function JsonFieldEditor({
  value,
  fieldId,
  onSave,
  readOnlyMode = false,
  globalModalId,
}: JsonFieldEditorProps) {
  // Map fieldId to the appropriate MODAL_IDS constant
  const getModalId = useCallback(() => {
    const fieldMap: Record<string, string> = {
      "social_links": MODAL_IDS.jsonEditors.socialLinks,
      "custom_fields": MODAL_IDS.jsonEditors.customFields,
      "current_technologies": MODAL_IDS.jsonEditors.currentTechnologies,
      "employee_size": MODAL_IDS.jsonEditors.employeeSize,
    };
    return fieldMap[fieldId] || globalModalId || `json-editor-${fieldId}`;
  }, [fieldId, globalModalId]);

  const modalId = getModalId();

  const [isSaving, setIsSaving] = useState(false);
  const [pairs, setPairs] = useState<JsonKeyValuePair[]>([]);
  const [isValid, setIsValid] = useState(true);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rawValue, setRawValue] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const globalModalStack = useGlobalModalStack();

  // Initialize pairs when editor opens
  const initializeEditor = useCallback(() => {
    const parsed = parseJSONSafely(value);

    if (parsed) {
      setPairs(objectToKeyValuePairs(parsed));
      setIsValid(true);
      setParseError(null);
      setRawValue("");
    } else {
      // Invalid JSON
      setIsValid(false);
      const error = getJSONParseError(typeof value === "string" ? value : JSON.stringify(value));
      setParseError(error || "Invalid JSON format");
      setRawValue(typeof value === "string" ? value : JSON.stringify(value));
      setPairs([]);
    }
  }, [value]);

  const existingKeys = useMemo(() => pairs.map((p) => p.key), [pairs]);

  const handleUpdatePair = useCallback((oldKey: string, newKey: string, newValue: JsonValue) => {
    setPairs((prev) => prev.map((p) => (p.key === oldKey ? { key: newKey, value: newValue } : p)));
    setIsValid(true);
    setParseError(null);
  }, []);

  const handleDeletePair = useCallback((keyToDelete: string) => {
    setPairs((prev) => prev.filter((p) => p.key !== keyToDelete));
  }, []);

  const handleAddPair = useCallback((key: string, value: JsonValue) => {
    setPairs((prev) => [...prev, { key, value }]);
  }, []);

  // Validate all pairs before saving
  const validateAllPairs = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    let hasErrors = false;

    pairs.forEach(({ key, value }, index) => {
      const validation = validateKeyValuePair(key, value);
      if (!validation.valid) {
        errors[`${key}-${index}`] = validation.error || "Invalid pair";
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  }, [pairs]);

  const handleSave = useCallback(async (onClose: () => void) => {
    try {
      setIsSaving(true);

      // Validate all pairs first
      if (!validateAllPairs()) {
        toast.error("Please fix validation errors before saving");
        setIsSaving(false);
        return;
      }

      const newValue = keyValuePairsToObject(pairs) as Record<string, JsonValue>;

      if (!readOnlyMode) {
        await onSave(newValue);
      }

      toast.success("JSON data saved successfully");
      onClose();
    } catch (error) {
      console.error("Error saving JSON data:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to save JSON data";
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [pairs, onSave, readOnlyMode, validateAllPairs]);

  const handleCancel = useCallback(() => {
    // Reset state when canceling
    setPairs([]);
    setIsValid(true);
    setParseError(null);
    setRawValue("");
    setValidationErrors({});
  }, []);

  return (
    <ModalContainer<JsonEditorModalData> modalId={modalId}>
      {({ isOpen, data, onClose }) => (
        <>
          {/* Trigger Button */}
          {!isOpen && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Edit JSON"
              onClick={() => {
                initializeEditor();
                globalModalStack.openModal(modalId);
              }}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}

          {/* Modal */}
          {isOpen && (
            <ResponsiveModal
              globalModalId={modalId}
              open={isOpen}
              onOpenChange={(open) => {
                if (!open) {
                  handleCancel();
                  onClose();
                }
              }}
              title={`Edit ${fieldId}`}
              description="Manage key-value pairs"
              maxWidth="2xl"
              showCloseButton={true}
            >
              <div className="space-y-4">
                {/* Invalid JSON Warning */}
                {!isValid && parseError && <JsonErrorDisplay rawValue={rawValue} error={parseError} />}

                {/* Validation Errors */}
                {Object.keys(validationErrors).length > 0 && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
                    <p className="text-sm font-semibold text-destructive mb-2">Validation Errors:</p>
                    <ul className="space-y-1">
                      {Object.entries(validationErrors).map(([key, error]) => (
                        <li key={key} className="text-xs text-destructive">
                          • {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key-Value Pairs List */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Pairs ({pairs.length})</p>
                  {pairs.length === 0 ? (
                    <div className="text-sm text-muted-foreground italic p-3 bg-muted rounded-md border border-dashed border-input">
                      No key-value pairs yet
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {pairs.map(({ key, value }) => (
                        <KeyValuePairRow
                          key={key}
                          keyName={key}
                          value={value}
                          onUpdate={(newKey, newValue) => handleUpdatePair(key, newKey, newValue)}
                          onDelete={() => handleDeletePair(key)}
                          existingKeys={existingKeys}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Pair Form */}
                <AddKeyValueForm onAdd={handleAddPair} existingKeys={existingKeys} isLoading={isSaving} />

                {/* Action Buttons */}
                <div className="flex gap-2 justify-end pt-2 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      handleCancel();
                      onClose();
                    }} 
                    disabled={isSaving} 
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSave(onClose)}
                    disabled={isSaving || Object.keys(validationErrors).length > 0}
                    size="sm"
                    title={
                      Object.keys(validationErrors).length > 0
                        ? "Fix validation errors before saving"
                        : ""
                    }
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </ResponsiveModal>
          )}
        </>
      )}
    </ModalContainer>
  );
}
